/* Interviews — schedule + feedback */
import { useEffect, useMemo, useState } from "react";
import api, { API as API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Calendar, Plus, Trash2, Video, MapPin, Star, Send, Download, Copy } from "lucide-react";
import { toast } from "sonner";

const MODES = [
  { v: "video", l: "Video call" },
  { v: "teams", l: "Microsoft Teams" },
  { v: "zoom", l: "Zoom" },
  { v: "meet", l: "Google Meet" },
  { v: "phone", l: "Phone" },
  { v: "onsite", l: "Onsite" },
  { v: "other", l: "Other" },
];

const STATUS_COLORS = {
  scheduled: "bg-sky-500", completed: "bg-emerald-500", cancelled: "bg-rose-500", no_show: "bg-amber-500", rescheduled: "bg-violet-500",
};

const RECOMMENDATIONS = [
  { v: "strong_hire", l: "Strong hire", color: "text-emerald-600" },
  { v: "hire", l: "Hire", color: "text-sky-600" },
  { v: "hold", l: "Hold", color: "text-amber-600" },
  { v: "no_hire", l: "No hire", color: "text-rose-600" },
  { v: "strong_no_hire", l: "Strong no hire", color: "text-rose-700" },
];

export default function Interviews() {
  const [interviews, setInterviews] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [creating, setCreating] = useState(false);
  const [feedbackFor, setFeedbackFor] = useState(null);

  const refresh = async () => {
    const [iv, rs] = await Promise.all([api.get("/interviews"), api.get("/resumes")]);
    setInterviews(iv.data || []);
    setResumes(rs.data || []);
  };
  useEffect(() => { refresh(); }, []);

  const upcoming = useMemo(() => interviews.filter((i) => i.status === "scheduled"), [interviews]);
  const past = useMemo(() => interviews.filter((i) => i.status !== "scheduled"), [interviews]);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold flex items-center gap-2"><Calendar className="size-3.5" /> Interviews</div>
          <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">Scheduling & feedback</h1>
          <p className="text-muted-foreground mt-1 text-sm">Schedule rounds with candidates, paste Teams/Zoom links, and capture structured feedback.</p>
        </div>
        <Button onClick={() => setCreating(true)} className="gap-2 lift" data-testid="iv-new"><Plus className="size-4" /> Schedule interview</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title={`Upcoming · ${upcoming.length}`} list={upcoming} resumes={resumes} onRefresh={refresh} onFeedback={setFeedbackFor} testid="iv-upcoming" />
        <Section title={`Past · ${past.length}`} list={past} resumes={resumes} onRefresh={refresh} onFeedback={setFeedbackFor} testid="iv-past" />
      </div>

      {creating && <CreateDialog resumes={resumes} onClose={() => { setCreating(false); refresh(); }} />}
      {feedbackFor && <FeedbackDialog interview={feedbackFor} onClose={() => { setFeedbackFor(null); refresh(); }} />}
    </div>
  );
}

function Section({ title, list, onRefresh, onFeedback, testid }) {
  const del = async (id) => {
    if (!window.confirm("Delete this interview?")) return;
    try { await api.delete(`/interviews/${id}`); toast.success("Deleted"); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const downloadIcs = async (iv) => {
    try {
      const token = localStorage.getItem("rf_token");
      const res = await fetch(`${API_BASE}/interviews/${iv.id}/ics`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `interview-${(iv.candidate_name || "candidate").replace(/\s+/g, "_")}.ics`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Calendar invite downloaded — attach to email or open in Outlook/Teams");
    } catch (e) { toast.error("Download failed"); }
  };
  const copyTeamsTemplate = async (iv) => {
    try {
      const r = await api.get(`/interviews/${iv.id}/teams-template`);
      const text = `Subject: ${r.data.subject}\n\n${r.data.body}\n\n— Instructions —\n${r.data.instructions}`;
      await navigator.clipboard.writeText(text);
      toast.success("Teams invite template copied to clipboard");
    } catch (e) { toast.error("Failed to fetch template"); }
  };
  return (
    <Card className="border-border" data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">{title}</div>
        {list.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No interviews here yet.</div>
        ) : (
          <div className="space-y-2">
            {list.map((iv) => (
              <div key={iv.id} className="border border-border rounded-md p-3" data-testid={`iv-${iv.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`size-1.5 rounded-full ${STATUS_COLORS[iv.status]}`} />
                  <span className="font-bold">{iv.candidate_name || "Candidate"}</span>
                  <span className="chip bg-secondary text-foreground text-[10px]">Round {iv.round}</span>
                  <span className="chip bg-secondary text-foreground text-[10px] uppercase">{iv.status}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {iv.scheduled_at ? new Date(iv.scheduled_at).toLocaleString() : "Not yet scheduled"} · {iv.duration_minutes}min · {iv.mode}
                </div>
                {iv.meeting_link && <a href={iv.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 mt-1"><Video className="size-3" /> Join meeting</a>}
                {iv.location && <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="size-3" /> {iv.location}</div>}
                {iv.feedback && <div className="text-xs mt-2 italic line-clamp-2">"{iv.feedback}"</div>}
                <div className="flex gap-1 mt-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => onFeedback(iv)} className="gap-1" data-testid={`iv-feedback-${iv.id}`}><Star className="size-3" /> Feedback</Button>
                  <Button size="sm" variant="outline" onClick={() => downloadIcs(iv)} className="gap-1" data-testid={`iv-ics-${iv.id}`} title="Download .ics — adds to Outlook / Teams / Google Calendar"><Download className="size-3" /> .ics</Button>
                  <Button size="sm" variant="outline" onClick={() => copyTeamsTemplate(iv)} className="gap-1" data-testid={`iv-teams-${iv.id}`} title="Copy a Microsoft Teams meeting invite template to clipboard"><Copy className="size-3" /> Teams template</Button>
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del(iv.id)} data-testid={`iv-del-${iv.id}`}><Trash2 className="size-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CreateDialog({ resumes, onClose }) {
  const [p, setP] = useState({
    resume_id: "", round: 1, title: "Interview", scheduled_at: "", duration_minutes: 30, mode: "video",
    meeting_link: "", location: "", interviewer_name: "", interviewer_email: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!p.resume_id) { toast.error("Pick a candidate"); return; }
    setSaving(true);
    try {
      await api.post("/interviews", p);
      toast.success("Interview scheduled");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" data-testid="iv-dialog">
        <DialogHeader><DialogTitle>Schedule interview</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={p.resume_id} onValueChange={(v) => setP({ ...p, resume_id: v })}>
            <SelectTrigger data-testid="iv-resume"><SelectValue placeholder="Pick a candidate…" /></SelectTrigger>
            <SelectContent>{resumes.slice(0, 50).map((r) => <SelectItem key={r.id} value={r.id}>{r.data?.name || r.title}</SelectItem>)}</SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Title (e.g. Technical round)" value={p.title} onChange={(e) => setP({ ...p, title: e.target.value })} data-testid="iv-title" />
            <Input type="number" min={1} max={10} placeholder="Round" value={p.round} onChange={(e) => setP({ ...p, round: Number(e.target.value) })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input type="datetime-local" value={p.scheduled_at} onChange={(e) => setP({ ...p, scheduled_at: e.target.value })} data-testid="iv-when" />
            <Input type="number" min={15} max={240} step={15} placeholder="Duration (min)" value={p.duration_minutes} onChange={(e) => setP({ ...p, duration_minutes: Number(e.target.value) })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={p.mode} onValueChange={(v) => setP({ ...p, mode: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Location / room" value={p.location} onChange={(e) => setP({ ...p, location: e.target.value })} />
          </div>
          <Input placeholder="Meeting link (Teams/Zoom/Meet URL)" value={p.meeting_link} onChange={(e) => setP({ ...p, meeting_link: e.target.value })} data-testid="iv-link" />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Interviewer name" value={p.interviewer_name} onChange={(e) => setP({ ...p, interviewer_name: e.target.value })} />
            <Input placeholder="Interviewer email" value={p.interviewer_email} onChange={(e) => setP({ ...p, interviewer_email: e.target.value })} />
          </div>
          <Textarea rows={3} placeholder="Notes / agenda" value={p.notes} onChange={(e) => setP({ ...p, notes: e.target.value })} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="iv-save">{saving ? "Saving…" : "Schedule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FeedbackDialog({ interview, onClose }) {
  const [p, setP] = useState({
    feedback: interview.feedback || "",
    technical_rating: interview.technical_rating || 0,
    communication_rating: interview.communication_rating || 0,
    culture_fit_rating: interview.culture_fit_rating || 0,
    recommendation: interview.recommendation || "",
    status: interview.status || "scheduled",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/interviews/${interview.id}`, p);
      toast.success("Feedback saved");
      onClose();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };
  const Rating = ({ label, val, set, testid }) => (
    <div>
      <div className="text-xs font-semibold mb-1">{label}</div>
      <div className="flex gap-1" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => set(n)} className={`size-7 rounded-md border ${val >= n ? "bg-amber-500 border-amber-600 text-white" : "border-border bg-card"}`} data-testid={`${testid}-${n}`}><Star className="size-3.5 mx-auto" /></button>
        ))}
      </div>
    </div>
  );
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[560px]" data-testid="iv-feedback-dialog">
        <DialogHeader><DialogTitle>Interview feedback · {interview.candidate_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Rating label="Technical" val={p.technical_rating} set={(v) => setP({ ...p, technical_rating: v })} testid="fb-tech" />
            <Rating label="Communication" val={p.communication_rating} set={(v) => setP({ ...p, communication_rating: v })} testid="fb-comm" />
            <Rating label="Culture fit" val={p.culture_fit_rating} set={(v) => setP({ ...p, culture_fit_rating: v })} testid="fb-culture" />
          </div>
          <div>
            <div className="text-xs font-semibold mb-1">Recommendation</div>
            <Select value={p.recommendation} onValueChange={(v) => setP({ ...p, recommendation: v })}>
              <SelectTrigger data-testid="fb-rec"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>{RECOMMENDATIONS.map((r) => <SelectItem key={r.v} value={r.v}><span className={r.color}>{r.l}</span></SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Textarea rows={5} placeholder="Detailed feedback / strengths / red flags" value={p.feedback} onChange={(e) => setP({ ...p, feedback: e.target.value })} data-testid="fb-text" />
          <Select value={p.status} onValueChange={(v) => setP({ ...p, status: v })}>
            <SelectTrigger data-testid="fb-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="no_show">No show</SelectItem>
              <SelectItem value="rescheduled">Rescheduled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="gap-2" data-testid="fb-save"><Send className="size-4" /> Save feedback</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
